import React, { useEffect, useRef, useState } from 'react';
import Input, { InputProps } from '../..';
import Frame from '../../../Frame';
import { IconAngle, IconLoader } from '../../styles';
import * as S from '../../styles';
import * as I from './styled';
import SVG from '../../../SVG';

export type DropdownItem<T = any> = {
  id: number | string;
  name: number | string;
  type?: string;
  data?: T;
};

export interface InputDropdownMultipleProps extends InputProps {
  items: DropdownItem[];
  selectedItems?: DropdownItem[];
  onSelected?: (selectedItems: DropdownItem[]) => void;
  loading?: boolean;
}

const InputDropdownMultiple = ({
  onChange,
  onBlur,
  onSelected,
  className,
  value,
  label,
  onFocus,
  placeholder = '',
  items = [],
  loading = false,
  mask = (value: any) => value,
  unmask = (value: any) => value,
  onDebouncedChange,
  fontSize,
  disabled = false,
  selectedItems: si = [],
  ...rest
}: InputDropdownMultipleProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const frameRef = useRef<HTMLLabelElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [frameHeight, setFrameHeight] = useState(0);
  const [hoverItem, setHoverItem] = useState(0);
  const [inputValue, setInputValue] = useState(value ?? '');
  const [inputWidth, setInputWidth] = useState(0);
  const [inputHeight, setInputHeight] = useState(0);
  const [inputTop, setInputTop] = useState(0);
  const notFoundMessage = 'Sem opções disponíveis';
  const [selectedItems, setSelectedItems] = useState<DropdownItem[]>(si);
  const filtered = items
    .filter((item) =>
      String(item.name)
        .toLowerCase()
        .includes(String(inputValue).toLowerCase()),
    )
    .filter(({ id }) => !selectedItems.find((selected) => selected.id === id));

  const filteredItems =
    filtered.length !== 0 ? filtered : [{ id: -1, name: notFoundMessage }];

  const isEmpty = filteredItems[0]?.name === notFoundMessage;

  useEffect(() => {
    setSelectedItems(si);
  }, [si]);

  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      setIsFocused(false);
      setInputValue('');
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (frameRef.current) {
      setFrameHeight(
        isFocused ? Math.min(frameRef.current.offsetHeight, 300) : 0,
      );
    }
  });

  useEffect(() => {
    if (inputRef.current) {
      setInputHeight(inputRef.current.offsetHeight);
      const { top } = inputRef.current.getBoundingClientRect();
      setInputTop(top);
    }
  }, [isFocused, selectedItems]);

  useEffect(() => {
    setInputValue((value as any) ?? '');
  }, [value]);

  const dropdownListTop = inputTop + inputHeight + 10;

  let timerId: NodeJS.Timeout;

  React.useEffect(() => {
    if (!onDebouncedChange) return;
    const handleInputChange = () => {
      clearTimeout(timerId);
      timerId = setTimeout(() => {
        onDebouncedChange({ target: { value: inputValue } } as any);
      }, 500);
    };
    handleInputChange();
    return () => {
      clearTimeout(timerId);
    };
  }, [isFocused, inputValue, selectedItems.length]);

  const handleRemoveItem = (id: number | string) => {
    if (disabled) return;
    const newSelectedItems = selectedItems.filter((item) => item.id !== id);
    setSelectedItems(newSelectedItems);
    onSelected?.(newSelectedItems);
  };

  const handleItemClick = (item: DropdownItem) => {
    if (disabled) return;
    if (!selectedItems.some((selected) => selected.id === item.id)) {
      const newSelectedItems = [...selectedItems, item];
      setSelectedItems(newSelectedItems);
      onSelected?.(newSelectedItems);
    }
  };

  const Selecteds = () => {
    return (
      <>
        {selectedItems.map((item) => (
          <I.SelectedItem key={item.id}>
            {item.name}
            <I.RemoveButton
              onClick={() => {
                handleRemoveItem(item.id);
              }}
            >
              <SVG.Icon.RoundedClose
                width={16}
                height={16}
                fill="rgba(255, 255, 255, 0.26)"
              />
            </I.RemoveButton>
          </I.SelectedItem>
        ))}
      </>
    );
  };

  return (
    <S.DropdownContainer
      ref={ref}
      className={`dropdown-container ${className}`}
    >
      <Input
        ref={inputRef}
        label={label}
        placeholder={placeholder}
        fontSize={fontSize}
        setFieldWidth={setInputWidth}
        LeftContent={<Selecteds />}
        inputStyle={{
          width: 0,
          minWidth: '50px',
          flexGrow: 1,
        }}
        inputLeftStyle={{
          display: 'flex',
          flexWrap: 'wrap',
        }}
        onFocus={(e) => {
          setIsFocused(true);
          onFocus && onFocus(e as any);
        }}
        IconRight={
          <div
            style={{
              padding: '0 10px',
            }}
          >
            {loading ? (
              <IconLoader width={20} height={20} />
            ) : (
              <IconAngle
                onClick={() => {
                  if (disabled) return;
                  setIsFocused(!isFocused);
                  if (!isFocused) {
                    inputRef.current?.focus();
                  }
                }}
                $isFocused={isFocused}
              />
            )}
          </div>
        }
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          onChange && onChange(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !isEmpty) e.preventDefault();
          let item: DropdownItem;
          let newHoverItem: number;
          switch (e.key) {
            case 'ArrowDown':
              newHoverItem = Math.min(filteredItems.length, hoverItem + 1);
              setHoverItem(newHoverItem);
              break;
            case 'ArrowUp':
              newHoverItem = Math.max(-1, hoverItem - 1);
              setHoverItem(newHoverItem);
              break;
            case 'Enter':
              if (isEmpty) {
                setIsFocused(false);
              } else {
                item = filteredItems[hoverItem];
                if (item && item.id !== -2) {
                  handleItemClick(item);
                }
              }
              break;
            default:
              break;
          }
        }}
        disabled={disabled}
        {...rest}
      />
      <S.DropdownList
        ref={listRef}
        $isFocused={isFocused}
        $height={frameHeight}
        $inputWidth={inputWidth}
        $top={dropdownListTop}
        $fontSize={fontSize}
      >
        {!isEmpty ? (
          filteredItems.map(({ id, name, type }, index) => (
            <S.DropdownItem
              key={index + 1}
              $isSelectedItem={mask(inputValue) === name}
              $isHover={hoverItem === index}
              onMouseEnter={() => {
                setHoverItem(index);
              }}
              onMouseLeave={() => {
                setHoverItem(-1);
              }}
              onClick={() => {
                if (!(id === -1 || id === -2)) {
                  const item = filteredItems[hoverItem];
                  handleItemClick(item);
                  inputRef.current?.focus();
                }
              }}
            >
              {`${name}${type ? ` - ${type}` : ''}`}
            </S.DropdownItem>
          ))
        ) : (
          <S.DropdownItem
            key={0}
            onClick={() => {
              inputRef.current?.blur();
              setIsFocused(false);
            }}
          >
            {notFoundMessage}
          </S.DropdownItem>
        )}
        <Frame width={listRef?.current?.offsetWidth} ref={frameRef}>
          {isEmpty ? (
            <S.DropdownItem key={0}>{notFoundMessage}</S.DropdownItem>
          ) : (
            filteredItems.map(({ name }, index) => (
              <S.DropdownItem key={index}>{name}</S.DropdownItem>
            ))
          )}
        </Frame>
      </S.DropdownList>
    </S.DropdownContainer>
  );
};

export default InputDropdownMultiple;
